import { ZOOM_MAX, ZOOM_MIN, zoomLabel } from '../../lib/tiles';
import { useSession } from '../../store/session';
import { useSettings } from '../../store/settings';
import { Choice, Section, Toggle, WidgetPage } from './controls';

const STYLE_HINTS: Record<string, string> = {
  standard: 'The usual OpenStreetMap',
  topo: 'Contours and hills',
  humanitarian: 'Bold, simple colours',
};

function ZoomSlider({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <label className="block">
      <div className="mb-1 flex justify-between text-sm">
        <span>{label}</span>
        <span className="muted">
          {zoomLabel(value)} ({value})
        </span>
      </div>
      <input
        type="range"
        min={ZOOM_MIN}
        max={ZOOM_MAX}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[var(--accent)]"
      />
    </label>
  );
}

/** Settings → Widgets → Location: where and when the photo on screen was taken, with a map. */
export function LocationTab() {
  const info = useSettings((s) => s.photoInfo);
  const update = useSettings((s) => s.update);
  const styles = useSession((s) => s.config?.map?.styles) ?? [];
  return (
    <WidgetPage
      label="Show location"
      description="Where and when the photo on screen was taken, with a map. It appears for photos that carry this information: OneDrive photos and photos added from this device."
      enabled={info.enabled}
      onChange={(enabled) => update('photoInfo', { enabled })}
    >
      <Section title="Show">
        <Toggle label="Town or city" checked={info.showCity} onChange={(showCity) => update('photoInfo', { showCity })} />
        <Toggle label="County or district" checked={info.showDistrict} onChange={(showDistrict) => update('photoInfo', { showDistrict })} />
        <Toggle label="Country" checked={info.showCountry} onChange={(showCountry) => update('photoInfo', { showCountry })} />
        <Toggle label="Date taken" checked={info.showDate} onChange={(showDate) => update('photoInfo', { showDate })} />
        <Toggle label="Time taken" checked={info.showTime} onChange={(showTime) => update('photoInfo', { showTime })} />
        <Toggle label="Map" checked={info.showMap} onChange={(showMap) => update('photoInfo', { showMap })} />
        <Toggle
          label="Keep the panel when a photo has no location (shows a short message)"
          checked={info.keepWhenEmpty}
          onChange={(keepWhenEmpty) => update('photoInfo', { keepWhenEmpty })}
        />
      </Section>
      {info.showMap && (
        <Section title="Map">
          {styles.length > 1 && (
            <Choice
              options={styles.map((m) => ({ id: m.id, name: m.name, description: STYLE_HINTS[m.id] }))}
              value={styles.some((m) => m.id === info.mapStyle) ? info.mapStyle : styles[0]!.id}
              onChange={(mapStyle) => update('photoInfo', { mapStyle })}
              columns={3}
            />
          )}
          <Toggle label="Dark map on dark themes" checked={info.mapDark} onChange={(mapDark) => update('photoInfo', { mapDark })} />
          <Choice
            options={[
              { id: 'fixed', name: 'Fixed zoom', description: 'The map stays at the zoom you choose' },
              { id: 'gradual', name: 'Zoom in slowly', description: 'From a wide view in to your zoom while the photo is shown' },
            ]}
            value={info.zoomMode}
            onChange={(zoomMode) => update('photoInfo', { zoomMode })}
          />
          {info.zoomMode === 'gradual' && (
            <ZoomSlider
              label="Start from"
              value={info.zoomFrom}
              onChange={(zoomFrom) => update('photoInfo', { zoomFrom, zoom: Math.max(info.zoom, zoomFrom) })}
            />
          )}
          <ZoomSlider
            label={info.zoomMode === 'gradual' ? 'Zoom in to' : 'Zoom'}
            value={info.zoom}
            onChange={(zoom) =>
              update('photoInfo', info.zoomMode === 'gradual' ? { zoom, zoomFrom: Math.min(info.zoomFrom, zoom) } : { zoom })
            }
          />
        </Section>
      )}
    </WidgetPage>
  );
}
